/**
 * Meldungen — die Uebersicht des Meldungsregisters.
 *
 * Page-scoped (kein geteiltes Bauteil → JSDoc-Kopf, kein Registry-Eintrag,
 * Regel 12). Eine LESEflaeche: kein Knopf loest eine Meldung aus — das tut
 * der Cron.
 *
 *   GET /api/meldungen            Liste
 *   GET /api/meldungen/fristen    verstrichene Fristen ohne versandte Meldung
 *
 * Spec: `docs/superpowers/specs/2026-10-12-meldungsregister-design.md`
 *
 * 🔴 WAS HIER NICHT VERHANDELBAR IST:
 *
 * 1. Die WOERTER kommen aus `./meldungenText` — keine Zeile traegt ein eigenes
 *    Literal. `ablage.liste()` liefert `art` und `status` als ROHWERTE; ohne die
 *    Abbildung stuenden `verpackg_jahresmengenmeldung` und `versandt` auf der
 *    Flaeche. 🔴 Der deutsche-Woerter-Waechter sieht das NICHT: er liest
 *    Literale im Quelltext, diese zwei sind Daten aus der API (Regel 17 §1).
 * 2. Der Status kommt UNVERAENDERT aus der API. Die Seite rechnet keinen Status
 *    nach — sie bildet ihn nur ab (`statusPill`).
 * 3. Eine leere Liste sagt das (`EmptyState reason="no-data"`) — sie bleibt
 *    nicht leer. Und „nicht geladen" ist `reason="error"`, nicht `no-data`:
 *    ein Fehler darf nicht wie „nichts da" aussehen. Der Fehlerzweig traegt
 *    deshalb einen Weg heraus (`onRetry`), keinen `window.location.reload`.
 * 4. Die zwei Abrufe sind GETRENNT. In EINEM `Promise.all` riss ein Fehler auf
 *    `/fristen` die ERFOLGREICHE Registerantwort mit — der Nutzer las „The
 *    declarations register is not responding.", waehrend das Register antwortete.
 *    Der Fristenblock ist eine Zusatzanzeige; sein Ausfall darf die Liste nicht
 *    kosten, und er darf auch nicht still verschwinden (dann saehe er aus wie
 *    „keine offenen Pflichten").
 * 5. Die Fristen stehen in einem EIGENEN Panel. Es sind Meldepflichten, nicht
 *    Steuerfristen — die stehen im Morgenbriefing.
 * 6. 🔴 DER CSV-KNOPF ERSCHEINT NUR, WENN ES EINE CSV GIBT (`m.csv_text`).
 *    WEEE schreibt keine (Spec §4B) und `csv_bytes` antwortet dort 404 — ohne
 *    die Bedingung meldete die Liste „The file could not be loaded." fuer eine
 *    Datei, die es NIE gab. Dafuer fuehrt `ablage.liste` die Spalte in ihrer
 *    SELECT-Liste, und das DETAIL traegt dieselbe Bedingung (Punkt 7 dort):
 *    eine Flaeche allein zu haerten liesse die andere die Sackgasse bleiben.
 */
import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { toast } from 'sonner'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert, Box, Button, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import PageContainer from '../components/common/PageContainer'
import Panel from '../components/common/Panel'
import Pill from '../components/common/Pill'
import EmptyState from '../components/common/EmptyState'
import TableViewport from '../components/common/TableViewport'
import ViewHeader from '../components/ViewHeader'
import TableSkeleton from '../Skeletons/TableSkeleton'
import { artText, datumDe, dokumentText, statusPill } from './meldungenText'
import { SP } from '../theme/tokens'

// 🔴 Die GESCHUETZTE Hausform — nicht die nackte. Beide gibt es im Baum
//    (42 geschuetzt, 82 nackt); nur diese funktioniert in BEIDEN Lagen: mit
//    gesetzter Variable absolut, OHNE sie leer → relativ → CRA-Proxy. Der
//    nackte Ausdruck ergibt ohne `frontend/.env.local` `undefined/api/…`
//    (gemessen: dieser Worktree hat keine solche Datei). Vorlage:
//    `frontend/src/ProductDetail.jsx:69`.
const API = (process.env.REACT_APP_API_BASE_URL || '')

// 🔴 Die Dateien liegen HINTER dem JWT-Tor. Ein nackter `href` auf `/api/...`
//    kann den Authorization-Header NICHT setzen — eine Browser-Navigation traegt
//    ihn nicht, und es gibt keinen Cookie- oder Query-Token-Weg
//    (`backend/global_auth_gate.py` fuehrt den Pfad nicht in `_PUBLIC_PREFIXES`;
//    `backend/auth_utils.py:179-184` liest ausschliesslich den Header). In dev
//    ist das UNSICHTBAR, weil `DEV_DISABLE_AUTH` das Tor oeffnet — in prod liefen
//    PDF und CSV in ein 401.
//
//    Der Weg ist der des Repos: axios traegt den Header
//    (`frontend/src/index.js:59-71`), also als **Blob** holen und ueber eine
//    Objekt-URL oeffnen. Vorbild: `frontend/src/pages/invoicing/KorrekturDialog.jsx`.
async function holeAlsBlob(pfad) {
  const { data } = await axios.get(pfad, { responseType: 'blob' })
  return URL.createObjectURL(data)
}

async function lade(pfad, dateiname) {
  // 🔴 Die Ablehnung darf nicht unbehandelt bleiben — sonst sieht der Nutzer
  //    NICHTS, wenn der Abruf scheitert (kein Fehler, keine Meldung). Und der
  //    Weg heraus ist der des Hauses (`sonner`), kein `window.alert`: der
  //    Katalog fuehrt `window.alert` als `dontUseFor` des Dialogmusters
  //    (`frontend/src/design/catalog.jsx:232`).
  try {
    const url = await holeAlsBlob(pfad)
    const a = document.createElement('a')
    a.href = url
    a.download = dateiname
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch {
    toast.error('The file could not be loaded.')
  }
}

export default function Meldungen() {
  const [meldungen, setMeldungen] = useState(null)
  const [fehler, setFehler] = useState(false)
  // 🔴 `null` heisst „noch nicht geladen", `[]` „geladen und leer" — der
  //    Unterschied ist der Grund, warum der Fristenblock nicht schon beim
  //    ersten Rendern „keine offenen Pflichten" behauptet.
  const [fristen, setFristen] = useState(null)
  const [fristenFehler, setFristenFehler] = useState(false)
  const [lauf, setLauf] = useState(0)

  useEffect(() => {
    let ab = false
    setFehler(false)
    axios.get(`${API}/api/meldungen`)
      .then((r) => { if (!ab) setMeldungen(r.data.meldungen) })
      .catch(() => { if (!ab) setFehler(true) })
    return () => { ab = true }
  }, [lauf])

  useEffect(() => {
    let ab = false
    setFristenFehler(false)
    axios.get(`${API}/api/meldungen/fristen`)
      .then((r) => { if (!ab) setFristen(r.data.offen) })
      .catch(() => { if (!ab) setFristenFehler(true) })
    return () => { ab = true }
  }, [lauf])

  /** Beide Abrufe neu — der eine Weg heraus fuer Liste UND Fristenblock. */
  const nochmal = () => setLauf((n) => n + 1)

  if (fehler) {
    return (
      <PageContainer>
        <EmptyState reason="error" message="The declarations register is not responding."
                    detail="The list could not be loaded." onRetry={nochmal} />
      </PageContainer>
    )
  }

  return (
    // 🔴 Der Abstand ZWISCHEN den Abschnitten kommt aus der Flaeche, nicht aus
    // den Abschnitten: `Panel` bringt bewusst kein `mb`/`mt` mit (JSDoc), und
    // `PageContainer` setzt keinen. Ohne die Luecke stossen zwei Panels mit
    // ihren Rahmen aneinander. Dieselbe Zeile wie `Monatsabschluss.jsx` (10
    // Seiten tragen sie) — `SP.section`, kein eigener Wert.
    <PageContainer boxSx={{ display: 'flex', flexDirection: 'column', gap: SP.section }}>
      <ViewHeader title="Declarations"
                  subtitle="Annual and monthly declarations with their figures, the document and the proof of dispatch." />

      {/* 🔴 Der Block erscheint, wenn es etwas zu SAGEN gibt — offene Pflichten
          ODER ein gescheiterter Abruf. Ein Fehler, der den Block einfach
          wegliesst, sieht aus wie „keine offenen Pflichten" (Regel 17 §1 in der
          Oberflaeche). `Array.isArray` statt `!== null`: eine Nutzlast ohne
          `offen` darf die Seite nicht an `.length` reissen. */}
      {(fristenFehler || (Array.isArray(fristen) && fristen.length > 0)) && (
        <Panel component="section" aria-labelledby="meldungen-fristen">
          <Typography id="meldungen-fristen" variant="subtitle1" component="h2" sx={{ mb: SP.tight }}>
            Overdue declarations
          </Typography>
          {fristenFehler ? (
            <Alert severity="warning"
                   action={<Button color="inherit" size="small" onClick={nochmal}>Try again</Button>}>
              The overdue declarations could not be loaded.
            </Alert>
          ) : (
            // 🔴 Eine LISTE, kein Stapel Absaetze (Regel-16 H4): gestapelte
            // `Typography`-Zeilen waren fuer Hilfsmittel ein Satz ohne
            // Gliederung. Hausform wie der Grundblock in `KorrekturDialog`.
            <Box component="ul" sx={{ m: 0, pl: SP.card, '& > li + li': { mt: SP.tight } }}>
              {fristen.map((f) => (
                <Typography key={`${f.art}-${f.periode}`} component="li" variant="body2">
                  {artText(f.art)} {f.periode} — due date was {datumDe(f.faellig_am)}
                </Typography>
              ))}
            </Box>
          )}
        </Panel>
      )}

      {/* 🔴 `pad="none" clip` + `TableViewport` ist die Hausform fuer ein
          DATEN-Panel, dessen Tabelle bis zum Rahmen rollen soll
          (`design/muster/ListenMuster.jsx:543`). Sieben Spalten in einem
          gepolsterten Panel ohne Rollbehaelter werden bei ≤600 px gestaucht —
          `theme.js` setzt keine `MuiTable`-Ueberlaufregel, und `Panel` setzt
          `overflow` nur bei `clip`. Der Kopf behaelt seine Polsterung, weil er
          sonst am Rahmen klebte. */}
      <Panel component="section" aria-labelledby="meldungen-register" pad="none" clip>
        <Typography id="meldungen-register" variant="subtitle1" component="h2"
                    sx={{ px: SP.card, pt: SP.card, mb: SP.tight }}>
          Register
        </Typography>
        {meldungen === null ? (
          // 🔴 Ein Geruest in der FORM der geladenen Tabelle (`Skeletons/`,
          //    anti-patterns §11) — es meldet den Ladezustand ueber
          //    `role="status"` + `aria-label` + `aria-busy`. `framed={false}`:
          //    der Rahmen ist hier das `Panel` darueber, die geladene Tabelle
          //    steht im `TableViewport`.
          <TableSkeleton columns={7} framed={false}
                         ariaLabel="Loading the declarations register" />
        ) : meldungen.length === 0 ? (
          <EmptyState reason="no-data" message="No declaration in the register yet."
                      detail="The first run adds a row here." />
        ) : (
          <TableViewport footerPadPx={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Recipient</TableCell>
                  {/* 🔴 „Sent on", nicht „Sent": diese Spalte traegt das DATUM,
                      der Status daneben traegt das WORT „Sent" (`statusPill`).
                      Ein nacktes „Sent" waere zweimal dieselbe Ueberschrift fuer
                      zwei verschiedene Dinge — und die Detailseite sagt an
                      derselben Stelle schon „Sent on …". */}
                  <TableCell>Sent on</TableCell>
                  <TableCell>Files</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {meldungen.map((m) => {
                  const p = statusPill(m.status)
                  // 🔴 Das WORT der Form kommt aus `meldungenText`, wie `artText`
                  //    und `statusPill` daneben. Vorher stand hier ein Literal
                  //    „PDF" — fuer eine WEEE-Meldung liegen hinter demselben
                  //    Knopf aber die Bytes einer Mail (`dokument_typ = 'eml'`).
                  const typ = m.dokument_typ || 'pdf'
                  return (
                    <TableRow key={m.id}>
                      <TableCell>{m.periode}</TableCell>
                      <TableCell>{artText(m.art)}</TableCell>
                      <TableCell>{m.fassung}</TableCell>
                      <TableCell>
                        <Pill tone={p.tone} bordered>{p.text}</Pill>
                      </TableCell>
                      <TableCell>{m.empfaenger}</TableCell>
                      <TableCell>{datumDe(m.versandt_am)}</TableCell>
                      <TableCell>
                        <Link component="button" type="button"
                              onClick={() => lade(`${API}/api/meldungen/${m.id}/dokument`,
                                                 `${m.art}-${m.periode}-f${m.fassung}.${typ}`)}>{dokumentText(typ)}</Link>
                        {/* 🔴 Der Trenner gehoert MIT dem Knopf: bleibt der Knopf
                            weg, stuende sonst „Email (.eml) ·  · Details". */}
                        {m.csv_text && (
                          <>
                            {' · '}
                            <Link component="button" type="button"
                                  onClick={() => lade(`${API}/api/meldungen/${m.id}/csv`,
                                                     `${m.art}-${m.periode}-f${m.fassung}.csv`)}>CSV</Link>
                          </>
                        )}
                        {' · '}
                        <Link component={RouterLink} to={`/declarations/${m.id}`}>Details</Link>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableViewport>
        )}
      </Panel>
    </PageContainer>
  )
}
